import { Theme } from '@emotion/react';
import { Flex, Shape, Skeleton, Title, styled } from '@teko/ds-desktop';
import { DownOutlined } from '@teko/ds-icon';
import { Select } from 'components/Select';
import { useTenantConfig } from 'contexts';
import { t } from 'i18n';
import { StarRating } from 'modules/product-detail/shared/ProductReview/StarRating';
import { IRatingData } from 'types';
import { ReviewItem, Star } from './ReviewItem';
import { ViewMoreReviewsButton } from './ViewMoreReviewsButton';

const STAR_RATING = [5, 4, 3, 2, 1];

interface IProductRatingListProps {
  ratingData: IRatingData;
}

export const ProductRatingList: React.FC<IProductRatingListProps> = ({
  ratingData,
}) => {
  const {
    data,
    reviews,
    hasMore,
    loadMore,
    noFilter,
    filterParams,
    setFilterParams,
    numberOfReviewsRemaining,
    setPage,
    isValidating,
  } = ratingData;
  const { epicOmni1643, isMobile } = useTenantConfig();
  const isSmallScreen = window.innerWidth < 360;

  return data ? (
    <Shape>
      <Flex direction={isMobile ? 'column' : 'row'} gap="s8" padding="s16">
        <Title level={2} color="textTitle">
          {t('Lọc đánh giá')}:
        </Title>
        <StyledFlex gap="s8">
          <FilterTab
            isActive={noFilter}
            onClick={() => {
              setFilterParams(undefined);
              setPage(1);
            }}
          >
            {t('Tất cả')}
          </FilterTab>
          <FilterTab
            isActive={filterParams?.hasImage}
            onClick={() => {
              setFilterParams({ hasImage: true });
              setPage(1);
            }}
          >
            {t('Có hình ảnh')}
          </FilterTab>
          <FilterTab
            isActive={filterParams?.hasVideo}
            onClick={() => {
              setFilterParams({ hasVideo: true });
              setPage(1);
            }}
          >
            {t('Có video')}
          </FilterTab>
          {isMobile && !epicOmni1643 ? (
            <Select
              placeholder={
                <Flex direction="row" gapX="s4" align="center">
                  {t('Sao')} <Star size={16} />
                </Flex>
              }
              options={STAR_RATING.map((star) => ({
                value: star,
                label: isSmallScreen ? (
                  <Flex direction="row" gapX="s4" align="center">
                    {star} <Star size={16} />
                  </Flex>
                ) : (
                  <StarRating value={star} max={star} starSize={15} />
                ),
              }))}
              defaultValue={filterParams?.rating}
              labelRender={(item) => (
                <Flex direction="row" gapX="s4" align="center">
                  {item.value} <Star size={16} />
                </Flex>
              )}
              suffixIcon={
                <DownOutlined
                  size={18}
                  style={{
                    marginTop: '11px',
                  }}
                />
              }
              onSelect={(value) => {
                setFilterParams({ rating: value });
                setPage(1);
              }}
              onInputKeyDown={(e) => {
                e.preventDefault();
              }}
              menuItemSelectedIcon={null}
              notFoundContent={null}
              selectItemPadding={!isSmallScreen ? '0 0.25rem' : '0 1rem'}
              selectHeight="36px"
              style={{
                border: '1px solid #E4E5F0',
                borderRadius: '1rem',
              }}
            />
          ) : (
            STAR_RATING.map((star, index) => (
              <FilterTab
                key={index}
                isActive={filterParams?.rating === star}
                onClick={() => {
                  setFilterParams({ rating: star });
                  setPage(1);
                }}
              >
                <Flex direction="row" gapX="s4" align="center">
                  {star} <Star size={20} />
                </Flex>
              </FilterTab>
            ))
          )}
        </StyledFlex>
      </Flex>

      {isValidating ? (
        <Skeleton height="0.75rem" width="80%" marginTop="s16" />
      ) : (
        <>
          {reviews.length
            ? reviews.map((review, index) =>
                review ? (
                  <ReviewItem
                    key={index}
                    reviewData={review}
                    showDivider={index < reviews.length - 1}
                  />
                ) : null,
              )
            : null}
        </>
      )}
      {hasMore && (
        <ViewMoreReviewsButton
          onClick={loadMore}
          numberOfReviews={numberOfReviewsRemaining}
        />
      )}
    </Shape>
  ) : null;
};

const FilterTab = styled.div(
  ({ isActive, theme }: { isActive?: boolean; theme?: Theme }) => `
  padding: 8px;
  border: 1px solid ${isActive ? theme?.colors.primary500 : '#0000000F'};
  background-color: ${isActive ? '#1435C31A' : '#fff'};
  border-radius: 999px;
  color: ${isActive ? theme?.colors.primary500 : theme?.colors.textTitle};
  font-size: 12px;
  line-height: 20px;
  font-weight: 400;
  align-items: center;
  justify-self: center;
  white-space: nowrap;
  cursor: pointer;
`,
);

const StyledFlex = styled(Flex)(
  () => `
  overflow-x: auto;
  flex-wrap: nowrap;
  max-width: 100%;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth; 
  scrollbar-width: none;
  -ms-overflow-style: none;
`,
);
